/*
 * Select: one choice from a short list, a native <select> in the Input's look (Field.tsx):
 * label 550 14/20 above (or visually hidden), field radius 8, --field-stroke, 36px high
 * (40 on a touch screen), text 400 14/20, a 16px caret on the right. Not in Figma as its own
 * component (the design's selects are MultiSelect); native for keyboard, screen readers and
 * phones. A long list with search is MultiSelect's job.
 */
import { useId } from 'react'
import type { SelectHTMLAttributes } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { caretDown16 } from './icons'

export interface SelectChoice {
    value: string
    label: string
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'children'> {
    label: string
    options: SelectChoice[]
    /** Keep the label for screen readers only (a toolbar filter). */
    hideLabel?: boolean
    error?: string
}

export function Select({ label, options, hideLabel, error, className, ...rest }: SelectProps) {
    const id = useId()
    return (
        <div className={cx('flex flex-col items-start gap-[8px]', className)}>
            <label htmlFor={id} className={cx('ss03 text-[14px] leading-[20px] font-[550] text-[var(--text-default)]', hideLabel && 'sr-only')}>
                {label}
            </label>
            <div className="relative w-full">
                <select
                    id={id}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${id}-error` : undefined}
                    className={cx(
                        'ss03-ss04 block h-[36px] w-full cursor-pointer appearance-none rounded-[8px] border-0 bg-[var(--surface-raised)] py-0 pr-[32px] pl-[12px] text-[14px] leading-[20px] font-normal text-[var(--text-default)] shadow-[var(--field-stroke)] outline-none',
                        'focus-visible:shadow-[var(--field-stroke),var(--focus-field)] disabled:cursor-not-allowed disabled:opacity-50 pointer-coarse:h-[40px] pointer-coarse:text-[length:var(--font-size-medium)]',
                    )}
                    {...rest}
                >
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <span className="pointer-events-none absolute top-1/2 right-[10px] -translate-y-1/2">
                    <Icon src={caretDown16} size={16} />
                </span>
            </div>
            {error && (
                <p id={`${id}-error`} role="alert" className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-danger)]">
                    {error}
                </p>
            )}
        </div>
    )
}
